import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApiClient } from '../../service/API';
import SearchableDropdown from '../TicketComponents/SearchableDropdown';
import NotificationModal from '../TicketComponents/NotificationModal';
import { getApiErrorMessage } from '../../service/errorHandler';
import '../../styling/ManageTicketAssign.css';

export default function AssignmentPreviewPage() {
  const api = useApiClient();
  const navigate = useNavigate();

  const [boUsers, setBoUsers] = useState([]);
  const [boUserEmail, setBoUserEmail] = useState('');
  const [ruleCategory, setRuleCategory] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const categories = [
    { label: '*', value: null, path: null },
    { label: 'By User', value: 'USER', path: 'user' },
    { label: 'By Company', value: 'DOMAIN', path: 'domain' },
    { label: 'By Timeline', value: 'TIMELINE', path: 'timeline' },
    { label: 'By Service', value: 'SERVICE', path: 'service' },
  ];

  useEffect(() => {
    loadBoUsers();
  }, []);

  const loadBoUsers = async () => {
    try {
      const { data } = await api.get('/users/bouser-list');
      setBoUsers(data || []);
    } catch (err) {
      setModalMessage(getApiErrorMessage(err, 'Failed to load BO users.'));
      setShowModal(true);
    }
  };

  const boOptions = boUsers.map(u => ({
    fullname: u.fullname || u.name || u.email,
    email: u.email,
  }));

  const getBoByEmail = email => boUsers.find(u => u.email === email);

  const goToCreateRule = path => {
    navigate(`/assignment-preview/manage-assign-ticket/${path}`);
  };

  const preview = async () => {
    setPreviewRows([]);
    setLoading(true);

    try {
      const boUser = getBoByEmail(boUserEmail);

      if (!boUser && boUserEmail) {
        throw new Error('Please select a valid BO user.');
      }

      const params = {};

      if (boUser) {
        params.targetUserId = boUser.id;
      }

      if (ruleCategory) {
        params.ruleCategory = ruleCategory;
      }

      const { data } = await api.get('/assignments/preview', { params });

      setPreviewRows(data || []);
    } catch (err) {
      setModalMessage(getApiErrorMessage(err, 'Failed to preview assignment rules.'));
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ma-container">
      <div className="ma-header">
        <h2 className="ma-title">Assignment Preview</h2>
      </div>

      <NotificationModal
        show={showModal}
        message={modalMessage}
        onClose={() => setShowModal(false)}
      />

      <div className="ma-preview">
        <h3 className="ma-title" style={{ fontSize: 16 }}>
          Create Assignment Rule
        </h3>

        <div className="ma-tabs">
          {categories.filter(e => e.label != '*').map(category => (
            <button
              key={category.value}
              type="button"
              className="ma-tabBtn"
              onClick={() => goToCreateRule(category.path)}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>

      <div className="ma-preview">
        <h3 className="ma-title" style={{ fontSize: 16 }}>
          Preview Assignment Rules
        </h3>

        <div className="ma-form">
          <div className="ma-formGroup">
            <label className="ma-label">BO User</label>
            <SearchableDropdown
              id="preview-bo-user"
              options={boOptions}
              selectedValue={boUserEmail}
              onSelectedChange={setBoUserEmail}
              placeholder="Select BO user"
            />
          </div>

          <div className="ma-formGroup">
            <label className="ma-label">Rule Category</label>
            <select
              className="ma-input"
              value={ruleCategory}
              onChange={e => {
                setRuleCategory(e.target.value);
                setPreviewRows([]);
              }}
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            className="ma-btn ma-btn--primary"
            onClick={preview}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Preview'}
          </button>
        </div>

        {previewRows.length === 0 ? (
          <p className="ma-empty">No assignment rules found.</p>
        ) : (
          <table className="ma-table">
            <thead>
              <tr>
                <th>Scope</th>
                <th>Source</th>
                <th>Assigned To</th>
              </tr>
            </thead>

            <tbody>
              {previewRows.map((row, index) => (
                <tr key={`${row.matchedScope}-${row.targetSource}-${index}`}>
                  <td>
                    <span className="badge bg-primary text-wrap">{row.matchedScope}</span>
                  </td>
                  <td>{row.targetSource}</td>
                  <td>{row.assigneeEmail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}